import { Download } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../components/Button';
import { IncidentTimeline } from '../components/IncidentTimeline';
import { ObservationTimeline } from '../components/ObservationTimeline';
import { MetricCard } from '../components/MetricCard';
import { useExam } from '../context/ExamContext';
import { exportReportPdf } from '../utils/exportReportPdf';

export function ReportPage() {
  const { state } = useExam();
  const reportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string>();

  async function downloadPdf() {
    if (!reportRef.current) return;
    setIsExporting(true);
    setExportError(undefined);
    try {
      await exportReportPdf(reportRef.current);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'PDF export failed.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-4xl font-black text-white">Final Report</h1>
        <Button icon={<Download className="h-4 w-4" />} loading={isExporting} onClick={downloadPdf}>
          {isExporting ? 'Preparing PDF' : 'Download PDF'}
        </Button>
      </div>
      {exportError && <p role="alert" className="mb-4 text-sm text-red-300">{exportError}</p>}
      <div ref={reportRef} className="space-y-6 bg-midnight p-4 text-slate-50">
        {state.candidate?.mode === 'public-demo' ? <p className="text-sm font-semibold uppercase text-neon-blue">Prototype Demo</p> : null}
        <div className="glass rounded-2xl p-5">
          <h2 className="font-semibold">Candidate Information</h2>
          <p className="mt-3">{state.candidate?.name ?? 'Demo Candidate'} · {state.candidate?.email ?? 'demo@example.com'}</p>
          <p className="text-sm text-slate-400">Started: {state.startedAt ? new Date(state.startedAt).toLocaleString() : 'Demo session'} · Finished: {state.finishedAt ? new Date(state.finishedAt).toLocaleString() : 'Not recorded'}</p>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <MetricCard label="Risk Score" value={state.metrics.riskScore} tone={state.metrics.riskScore > 60 ? 'red' : 'amber'} />
          <MetricCard label="Attention" value={`${state.metrics.attentionScore}%`} tone="green" />
          <MetricCard label="Incidents" value={state.incidents.length} />
          <MetricCard label="Recommendation" value={state.metrics.riskScore > 65 ? 'Review' : 'Pass'} tone={state.metrics.riskScore > 65 ? 'red' : 'green'} />
        </div>
        <div className="glass rounded-2xl p-5">
          <h2 className="mb-4 font-semibold">Incident Timeline</h2>
          <IncidentTimeline incidents={state.incidents} />
        </div>
        <div className="glass rounded-2xl p-5">
          <h2 className="mb-4 font-semibold">Observation Timeline</h2>
          <ObservationTimeline observations={state.observations} />
        </div>
      </div>
    </section>
  );
}
